import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAlertes, marquerAlerteVue, marquerAlertesVues } from '../services/api';

/**
 * Le bandeau des alertes, dans la barre latérale.
 *
 * Un courriel qui ne part pas ne fait aucun bruit : la validation suit son
 * cours, le dossier avance, et c'est le client qui nous apprend des jours plus
 * tard qu'il n'a rien reçu. L'échec était pourtant journalisé — il fallait
 * seulement penser à ouvrir la page.
 *
 * Il s'affiche donc là où on ne peut pas le manquer, sur toutes les pages, et
 * il reste jusqu'à ce qu'on le marque vu. Rien à voir : rien ne s'affiche.
 */
export default function Alertes({ collapsed }) {
  const [alertes, setAlertes] = useState([]);
  const navigate = useNavigate();

  const relire = useCallback(() => {
    getAlertes().then(r => setAlertes(r.alertes || [])).catch(() => {});
  }, []);

  useEffect(() => {
    relire();
    // Une minute : assez pour prévenir pendant qu'on travaille, assez peu pour
    // ne pas interroger le serveur sans raison.
    const t = setInterval(relire, 60000);
    return () => clearInterval(t);
  }, [relire]);

  if (!alertes.length) return null;

  const vu = async (id) => {
    setAlertes(l => l.filter(a => a.id !== id));
    try { await marquerAlerteVue(id); } catch { relire(); }
  };

  const toutVu = async () => {
    setAlertes([]);
    try { await marquerAlertesVues(); } catch { relire(); }
  };

  if (collapsed) {
    return (
      <div title={`${alertes.length} alerte(s)`} onClick={() => navigate(alertes[0].lien || '/')}
        style={{ margin: '6px auto', width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
                 background: '#b91c1c', color: '#fff', fontSize: 12, fontWeight: 800,
                 display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {alertes.length}
      </div>
    );
  }

  const quand = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ margin: '8px 12px', border: '1px solid #b91c1c', borderRadius: 8,
                  background: 'rgba(185,28,28,0.10)', padding: '8px 9px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          {alertes.length} alerte{alertes.length > 1 ? 's' : ''}
        </span>
        <button type="button" onClick={toutVu}
          style={{ fontSize: 10, padding: '1px 6px', borderRadius: 5, cursor: 'pointer',
                   border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text3)' }}>
          tout vu
        </button>
      </div>

      <div style={{ display: 'grid', gap: 7, marginTop: 7 }}>
        {alertes.slice(0, 4).map(a => (
          <div key={a.id} style={{ fontSize: 11, lineHeight: 1.35 }}>
            <div onClick={() => a.lien && navigate(a.lien)}
              style={{ fontWeight: 700, color: 'var(--text)', cursor: a.lien ? 'pointer' : 'default' }}>
              {a.titre}
            </div>
            {a.detail && (
              <div style={{ color: 'var(--text3)', wordBreak: 'break-word' }}>{a.detail}</div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 2 }}>
              <span style={{ fontSize: 10, color: 'var(--text3)' }}>{quand(a.at)}</span>
              <button type="button" onClick={() => vu(a.id)}
                style={{ fontSize: 10, padding: 0, border: 'none', background: 'none',
                         color: '#b91c1c', cursor: 'pointer', textDecoration: 'underline' }}>
                vu
              </button>
            </div>
          </div>
        ))}
        {alertes.length > 4 && (
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>
            et {alertes.length - 4} autre{alertes.length - 4 > 1 ? 's' : ''}…
          </div>
        )}
      </div>
    </div>
  );
}
